import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

const grad = "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Bienvenue ${u.name}`);
      nav(u.role === "super_admin" ? "/super-admin" : "/admin");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-root" style={{ minHeight: "100vh", background: "#f4f6ff", fontFamily: "Inter, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1.5rem" }}>
      {/* Blobs */}
      <div style={{ position: "fixed", top: "-10rem", right: "-10rem", width: "500px", height: "500px", borderRadius: "50%", background: "rgba(139,92,246,.10)", filter: "blur(100px)", pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: "-10rem", left: "-10rem", width: "400px", height: "400px", borderRadius: "50%", background: "rgba(59,130,246,.08)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: "440px" }}>
        <Link to="/" data-testid="login-back"
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: "#4a4a6a", textDecoration: "none", marginBottom: "2rem" }}>
          <ArrowLeft size={14} /> Retour à l'accueil
        </Link>

        <div style={{ background: "#fff", border: "1px solid #e2e8f8", borderRadius: "24px", padding: "2.5rem", boxShadow: "0 8px 40px rgba(59,130,246,.08)" }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "2rem" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "16px", boxShadow: "0 4px 12px rgba(59,130,246,.4)" }}>N</div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#0d0d1a" }}>
              Neobk<span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
            </span>
          </div>

          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.02em", color: "#0d0d1a", marginBottom: "0.4rem" }}>
            Connexion
          </h1>
          <p style={{ color: "#4a4a6a", fontSize: "14px", marginBottom: "2rem" }}>Accédez à votre tableau de bord.</p>

          <form onSubmit={submit} data-testid="login-form" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#0d0d1a", marginBottom: "6px" }}>Email</label>
              <input
                type="email" value={email} required onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@example.com"
                data-testid="login-email"
                style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f8", fontSize: "14px", color: "#0d0d1a", outline: "none", background: "#f9faff", boxSizing: "border-box" }}
                onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f8"}
              />
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ fontSize: "13px", fontWeight: 600, color: "#0d0d1a" }}>Mot de passe</label>
                <Link to="/forgot-password" style={{ fontSize: "12px", color: "#3B82F6", textDecoration: "none", fontWeight: 500 }}>
                  Mot de passe oublié ?
                </Link>
              </div>
              <input
                type="password" value={password} required onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f8", fontSize: "14px", color: "#0d0d1a", outline: "none", background: "#f9faff", boxSizing: "border-box" }}
                onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) => e.target.style.borderColor = "#e2e8f8"}
              />
            </div>
            <button
              type="submit" disabled={busy}
              data-testid="login-submit"
              style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "none", cursor: busy ? "not-allowed" : "pointer", fontWeight: 600, fontSize: "15px", color: "#fff", background: busy ? "#a5b4fc" : grad, boxShadow: busy ? "none" : "0 8px 24px rgba(59,130,246,.4)", transition: "all .2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              {busy ? "Connexion…" : <><span>Se connecter</span><ArrowRight size={16} /></>}
            </button>
          </form>

          <div style={{ marginTop: "1.5rem", textAlign: "center", fontSize: "13px", color: "#4a4a6a" }}>
            Pas encore de compte ?{" "}
            <Link to="/register" data-testid="login-register-link" style={{ color: "#3B82F6", fontWeight: 600, textDecoration: "none" }}>
              Inscription
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
